import { Injectable } from '@angular/core';
import { RequestCallBottomsheetComponent } from '@shared/bottom-sheet-layouts/pending-call-bottomsheet/request-call-bottomsheet/request-call-bottomsheet.component';
import { take } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TimeService } from '@services/time-service';
import { ConnectionService } from '@services/connection-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { AppConfig } from '../../app/app.config';
import { EligibilityValidatorService } from './eligibility-validator.service';
import { UserService } from '@services/user';
import { TIME_SLOTS, DEFAULT_HOURS, FALLBACK_CRITERIA } from './constants';
import { PendingCallBannerState } from '@services/pending-call-banner-service/types';
import {
  TimeSlot,
  UnpaidUserDoctorCallVariant,
  PendingCall,
  PendingCallState,
  RequestCallData,
  OSConfig
} from './types';

@Injectable({
  providedIn: 'root',
})
export class UnpaidPendingCallService {
  private osInfo: string;
  private readonly _callCreated: Subject<void> = new Subject<void>();
  private readonly sheetRefs: MatBottomSheetRef<any>[] = [];
  private hasShownBottomSheetInSession: boolean = false;

  constructor(
    private readonly bottomSheet: MatBottomSheet,
    private readonly timeService: TimeService,
    private readonly appConfig: AppConfig,
    private readonly conn: ConnectionService,
    private readonly appWebBridge: AppWebBridgeService,
    private readonly eligibilityValidator: EligibilityValidatorService,
    private readonly userService: UserService
  ) {
    this.appWebBridge?.requestOSInformation().then((osInfo) => this.osInfo = osInfo);
  }

  get callCreated$(): Observable<void> {
    return this._callCreated.asObservable();
  }

  get hasShownBottomSheet(): boolean {
    return this.hasShownBottomSheetInSession;
  }

  notifyCallCreated(): void {
    this._callCreated.next();
  }

  initializeTAT(experimentVariantData: UnpaidUserDoctorCallVariant): { hours: number; slot: TimeSlot } {
    const hours = experimentVariantData?.noOfHours ?? DEFAULT_HOURS;
    const hhmm = this.timeService.getMilitaryTime();
    const slot = this.determineSlot(hhmm);

    return { hours, slot };
  }

  openRequestCallBottomsheet(regimenId: string, regimenClass: string, noOfHours: number, slot: TimeSlot, rehaulView?: boolean): Promise<boolean> {
    const data: RequestCallData = { regimenId, regimenClass, noOfHours, slot, rehaulView };

    const sheetRef = this.bottomSheet.open(
      RequestCallBottomsheetComponent, {
        data, disableClose: true,
        panelClass: 'request-call-bottomsheet-panel',
      }
    );

    if (rehaulView) this.hasShownBottomSheetInSession = true;
    this.sheetRefs.push(sheetRef);

    this.handleBackdropClick(sheetRef);

    return this.handleSheetDismissal(sheetRef);
  }

  private handleBackdropClick(sheetRef: MatBottomSheetRef): void {
    sheetRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => {
        document.activeElement instanceof HTMLElement && document.activeElement.blur();
        sheetRef.dismiss();
      });
  }

  private handleSheetDismissal(sheetRef: MatBottomSheetRef): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      sheetRef
        .afterDismissed()
        .pipe(take(1))
        .subscribe(result => {
          if (result?.pendingCallCreated) {
            this.notifyCallCreated();
            resolve(true);
          } else {
            resolve(false);
          }
        });
    });
  }

  closeAllBottomsheets(): void {
    this.sheetRefs.forEach(ref => ref.dismiss());
    this.sheetRefs.length = 0;
  }

  checkEligibility(
    variantData: UnpaidUserDoctorCallVariant,
    user: Table.User,
    regimenClass: string,
  ): boolean {
    if (!variantData) return false;
    if (!this.userService.isUnpaidUser(user)) return false;

    const osConfig: OSConfig = this.getOSConfig(variantData);
    if (!osConfig) {
      return this.osInfo?.toLowerCase() === 'web';
    }

    const userPhoneModel: string = this.extractPhoneModel(user?.get('phoneDetails'));
    return this.eligibilityValidator.validateCriteria(user, osConfig, regimenClass, userPhoneModel);
  }

  private getOSConfig(variantData: UnpaidUserDoctorCallVariant): OSConfig | undefined {
    const osLower: string = this.osInfo?.toLowerCase() || '';

    if (variantData.criteria?.[osLower] !== undefined) {
      return variantData.criteria[osLower];
    }

    return FALLBACK_CRITERIA[osLower as keyof typeof FALLBACK_CRITERIA];
  }

  private extractPhoneModel(phoneDetails?: string): string {
    if (!phoneDetails) return '';

    const userPhoneDetails = phoneDetails
      .split(',')
      .find((part: string) => part.trim().toLowerCase().startsWith('model:'));

    return userPhoneDetails ? userPhoneDetails.split(':')[1].trim() : '';
  }

  determineSlot(hhmm: string): TimeSlot {
    if (hhmm >= TIME_SLOTS.WORK_START && hhmm <= TIME_SLOTS.WORK_END) {
      return 'working';
    } else if (hhmm >= TIME_SLOTS.EVENING_START) {
      return 'evening';
    } else {
      return 'early';
    }
  }

  async getFilteredPendingCalls(regimenId: string): Promise<PendingCall[]> {
    const allCalls = await this.conn.getAllPendingCallsForType(
      this.appConfig.Shared.PendingCall.Type.UNPAID_USER_DOCTOR_CALL,
    );

    return allCalls.filter((call: PendingCall) => call.referenceId === regimenId);
  }

  async determinePendingCallState(pendingCalls: PendingCall[]): Promise<PendingCallState> {
    if (!pendingCalls.length) {
      return { status: null, hideBanner: false };
    }

    const { CallCompleted, Requested, NotReachable, CallLater } = this.appConfig.Shared.PendingCall.Status;

    if (pendingCalls.some((call) => call.status === CallCompleted)) {
      return { status: CallCompleted, hideBanner: false };
    }

    const selectedCall = this.getRelevantCall(pendingCalls, Requested);
    return this.evaluateCallState(selectedCall, NotReachable, CallLater);
  }

  async calculateBannerState(
    variantData: UnpaidUserDoctorCallVariant,
    user: Table.User,
    regimenClass: string,
    pendingCalls: PendingCall[] | null | undefined,
    isRegimenPreparationTimePending: boolean = false
  ): Promise<PendingCallBannerState> {
    const isEligible = this.checkEligibility(variantData, user, regimenClass) && !isRegimenPreparationTimePending;
    const { status, hideBanner } = await this.determinePendingCallState(pendingCalls || []);

    return {
      isEligible,
      status,
      hideBanner,
      variantData,
      checkComplete: true
    };
  }

  private getRelevantCall(pendingCalls: PendingCall[], requestedStatus: string): PendingCall {
    if (pendingCalls.length === 1) {
      return pendingCalls[0];
    }

    const latestCall = pendingCalls[pendingCalls.length - 1];
    return latestCall.status === requestedStatus
      ? pendingCalls[pendingCalls.length - 2]
      : latestCall;
  }

  private evaluateCallState(
    call: PendingCall,
    notReachableStatus: string,
    callLaterStatus: string
  ): PendingCallState {
    if (![notReachableStatus, callLaterStatus].includes(call.status)) {
      return { status: call.status, hideBanner: false };
    }

    const daysDifference = this.timeService.differenceInDays(
      new Date(call.createdAt),
      new Date()
    );

    return daysDifference >= 3
      ? { status: null, hideBanner: true }
      : { status: call.status, hideBanner: false };
  }
}
